import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import { useContext } from 'react';
import { i as EGDSLayoutFlex, j as EGDSLayoutFlexItem } from '../layout-flex-item/index-chunk.esm.js';
import { c as classNames, u as useEGDSContext } from '../theme-provider/index-chunk.esm.js';
import styles from '../styles/uitk-egds-radio-button.css';
import { a as EGDSIcon, E as EGDSIconSize } from '../icon/index-chunk.esm.js';
const EGDSRadioButtonGroupContext = React.createContext(null);
const getClassNameList$1 = classNames.bind(styles);
const EGDSRadioButton = props => {
  let useReducedMotion = false;
  if (typeof window !== 'undefined') {
    useReducedMotion = window.matchMedia(`(prefers-reduced-motion: reduce)`).matches;
  }
  const {
    className,
    children,
    checked,
    description,
    disabled,
    id,
    label,
    labelClassName,
    labelSuffix,
    required,
    name,
    value,
    a11yLabel,
    onChange
  } = props;
  const RadioButtonGroupContext = useContext(EGDSRadioButtonGroupContext);
  const baseCssClass = 'uitk-radio-button';
  const CssClassNameLabelSuffix = labelSuffix ? `${baseCssClass}-label-haslabelsuffix` : null;
  const wrapperClassNames = getClassNameList$1(baseCssClass, {
    'is-disabled': disabled
  });
  const labelCssClassNames = getClassNameList$1(`uitk-radio-button-label`, CssClassNameLabelSuffix, {
    [labelClassName]: Boolean(labelClassName),
    'is-required': required
  });
  const inputClassNames = getClassNameList$1({
    checked
  }, className);
  const contents = label || children;
  const checkedProp = RadioButtonGroupContext?.defaultChecked ? 'defaultChecked' : 'checked';
  const isChecked = checked || RadioButtonGroupContext?.selected === value;
  const controlClassNames = getClassNameList$1(`${baseCssClass}-control`);
  const reducedMotionClassNames = getClassNameList$1(`${baseCssClass}-has-reduced-motion`);
  const {
    data: contextData
  } = useEGDSContext();
  const {
    hasRequiredFieldIndicator
  } = contextData ?? {};
  const randomId = Math.random().toString(36).substring(7);
  const uniqueDescriptionId = `radio-button-description-${randomId}`;
  const a11yContent = a11yLabel ?? contents?.toString();
  const a11yFullLabel = labelSuffix ? `${a11yContent} ${labelSuffix}` : a11yContent;
  return jsx(EGDSLayoutFlex, {
    className: wrapperClassNames,
    wrap: "nowrap",
    children: jsx(EGDSLayoutFlexItem, {
      className: getClassNameList$1(`${baseCssClass}-content`),
      grow: 1,
      children: jsx("div", {
        children: jsxs("label", {
          className: labelCssClassNames,
          htmlFor: id,
          children: [jsx("input", {
            type: "radio",
            name: name || RadioButtonGroupContext?.group,
            id: id,
            [checkedProp]: isChecked,
            className: inputClassNames,
            onChange: onChange || RadioButtonGroupContext?.onChange,
            value: value,
            disabled: disabled,
            "aria-label": a11yFullLabel,
            "aria-describedby": description ? uniqueDescriptionId : ''
          }), jsx("span", {
            "aria-hidden": "true",
            className: useReducedMotion ? `${controlClassNames} ${reducedMotionClassNames}` : controlClassNames
          }), jsxs("span", {
            "aria-hidden": Boolean(a11yLabel),
            className: getClassNameList$1(`${baseCssClass}-label-content`),
            children: [contents, required && hasRequiredFieldIndicator && jsxs("span", {
              className: getClassNameList$1('uitk-radio-button-required-asterisk'),
              "aria-hidden": "true",
              children: [' ', "*"]
            })]
          }), labelSuffix && jsx("span", {
            "aria-hidden": Boolean(a11yLabel),
            className: getClassNameList$1(`${baseCssClass}-label-suffix`),
            children: labelSuffix
          }), a11yLabel && jsx("span", {
            className: "is-visually-hidden",
            children: a11yLabel
          }), description && jsx("span", {
            className: getClassNameList$1(`${baseCssClass}-description uitk-type-200`),
            id: uniqueDescriptionId,
            children: description
          })]
        })
      })
    })
  });
};
const getClassNameList = classNames.bind(styles);
const EGDSRadioButtonGroup = props => {
  const {
    children,
    className,
    defaultChecked,
    errorMessage,
    group,
    legendLabel,
    selected,
    onChange
  } = props;
  const wrapperClassNames = getClassNameList('uitk-radio-button-group', className);
  const errorClassNames = getClassNameList('uitk-radio-button-error');
  const {
    data: contextData
  } = useEGDSContext();
  const {
    hasRequiredFieldIndicator,
    collapsed,
    srMessage
  } = contextData ?? {};
  const radioButtonGroupContextValue = React.useMemo(() => ({
    onChange,
    defaultChecked,
    group,
    selected,
    hasRequiredFieldIndicator
  }), [onChange, defaultChecked, group, selected, hasRequiredFieldIndicator]);
  return jsxs("fieldset", {
    className: wrapperClassNames,
    children: [jsxs("legend", {
      className: "is-visually-hidden",
      children: [legendLabel || group, collapsed && (srMessage ? `. ${srMessage}` : '')]
    }), errorMessage && jsxs(EGDSLayoutFlex, {
      space: "xsmall",
      alignItems: "center",
      className: errorClassNames,
      children: [jsx(EGDSLayoutFlexItem, {
        alignSelf: "start",
        children: jsx(EGDSIcon, {
          name: "error_outline",
          size: EGDSIconSize.SMALL
        })
      }), errorMessage]
    }), jsx(EGDSRadioButtonGroupContext.Provider, {
      value: radioButtonGroupContextValue,
      children: children
    })]
  });
};
export { EGDSRadioButton as E, EGDSRadioButtonGroup as a };